// Render functions for the dashboard, terminal lists, logs, groups, users.
//
// All render functions read state from window.timestop.state (passed in as
// a parameter so they're testable in isolation) and write to the DOM
// via standard element-id lookups that the shell.php HTML provides.
//
// During the migration window, the inline scripts in admin/index.php
// keep their own `let terminals` etc. — those lets INITIALISE from
// window.timestop.state, so the local `terminals` name points at the SAME
// object as window.timestop.state.terminals. Mutations via the local
// name therefore mutate the bundle's view too.

import {
  escapeHtml,
  displayTerminalIcon,
  displayTerminalName,
  extractLogDate,
  formatDuration,
  formatLocalDate,
  formatTs,
  getCurrencyByISO,
  groupLogsByDate,
  renderBucketHtml,
  renderNotifyBucket,
  terminalLogRow,
  terminalGlobalLogRow,
  terminalNotificationLogRow,
  logKey,
  driftSrv2Local,
  lastSeenFor,
} from './utils';
import type { TimestopState } from './state';
import type { Group, LogEntry, LoginAttempt, Terminal, TerminalsById } from './types';
import {formatDividedCurrency} from "./utils/currency";
import { apiFetch, type ApiFetchConfig } from './api';
// i18n t() helper. Aliased to `i18n` rather than the natural
// short name `t` because render.ts uses `t` as a Terminal
// loop variable in three for-of blocks and one .map callback
// (legacy from the inline script). Calling the helper `i18n(...)`
// reads as a domain verb and avoids shadowing the Terminal
// variable.
import { t as i18n } from './i18n';

// =====================================================================
// Helpers
// =====================================================================

interface Bucket {
  key: string;
  name: string;
  terminals: Terminal[];
}

// Group terminals by their oid (operator group). Unassigned bucket
// comes first, then one bucket per group in the order they appear in
// the `groups` array. Used by both the Quick Terminals strip and the
// All Terminals page.
export function groupTerminalsByOid(state: TimestopState): Bucket[] {
  const byId = Object.create(null);
  for (const g of state.groups) byId[String(g.id)] = g;

  const buckets = new Map<string, Bucket>();
  const ensure = (key: string, name: string): Bucket => {
    if (!buckets.has(key)) buckets.set(key, { key, name, terminals: [] });
    return buckets.get(key)!;
  };

  ensure('__unassigned__', i18n('terminal_list.group_unassigned'));
  for (const g of state.groups) ensure(String(g.id), g.name || `Group ${g.id}`);

  for (const t of Object.values(state.terminals)) {
    const raw = t.oid;
    const oid = (raw === null || raw === undefined || raw === '' || raw === 0 || raw === '0')
      ? null
      : String(raw);
    const meta = oid !== null ? byId[oid] : null;
    if (oid !== null && meta) {
      ensure(oid, meta.name || `Group ${oid}`).terminals.push(t);
    } else {
      ensure('__unassigned__', i18n('terminal_list.group_unassigned')).terminals.push(t);
    }
  }

  return Array.from(buckets.values());
}

// =====================================================================
// renderAccBlocks -- CASH (coin+note) / CASHLESS (card) / HOPPERS OUT
// for the All Terminals card middle section. Mirrors the Overview
// tab's per-ISO grid-cols-3 balance block, but built from acc_long
// (long period = since last reset, matches the Overview tab's
// "Total IN" source) so the two views stay consistent.
//
// One card-row per section: a gray rounded-2xl box with the label,
// a <hr>, and a grid of per-ISO cells. Hopper counts are an
// up-to-grid-cols-3 row of 3 hoppers.
//
// `t` is the Terminal row. State is forwarded so DB-backed currency
// defaults (state.currencyDefaults) are honoured for any ISO the
// terminal didn't declare in its own cur_recv.
// =====================================================================

const ACC_SECTION_BOX = 'bg-gray-100 rounded-2xl p-2 text-center';
// Inline `display: grid` + `grid-template-columns: repeat(N, minmax(0, 1fr))`
// because CASH and CASHLESS need to match the terminal's ISO count
// (1, 2, 3, 4, ...) instead of being pinned at the static
// `grid-cols-3` Tailwind utility. We avoid `grid-cols-[repeat(var(--x),...)]`
// (a CSS-var-driven arbitrary value) because the BO's prebuilt
// `admin/tailwind.min.css` only ships a small subset of utilities
// and the CDN fallback can lag on JIT for nested arbitrary values.
// Inline style is portable across both the CDN build and the
// prebuilt min.css and is what `grid-cols-3` already produces
// under the hood. HOPPERS OUT still uses a fixed 3 columns because
// there are always exactly 3 hoppers.
const ACC_LABEL = 'text-xs text-gray-500';

function isoGridStyle(n: number): string {
  // Clamp to at least 1 (an empty CASH row shouldn't render as a
  // 0-column grid, which the browser would discard) and at most
  // some sane upper bound so a terminal with 12 ISOs doesn't end
  // up with 12 squished cells.
  const cols = Math.max(1, Math.min(n, 6));
  return `display:grid;grid-template-columns:repeat(${cols},minmax(0,1fr));gap:0.5rem;font-weight:500;overflow:hidden;`;
}

function isoList(t: Terminal): string[] {
  // Use the terminal's declared currency order (cur_recv) — that's
  // the same set the Overview tab's balances block shows, and
  // matches the set the spot terminal actually accepts.
  const out: string[] = [];
  for (const cfg of Object.values(t.cur_recv || {})) {
    if (cfg && cfg.ISO) out.push(String(cfg.ISO));
  }
  return out;
}

function moneyInFor(t: Terminal, kind: 'coin' | 'note' | 'card'): Record<string, number> {
  const acc = t.acc_long;
  if (!acc || !acc.in) return {};
  const slot = (acc.in as Record<string, Record<string, number> | undefined>)[kind];
  return slot || {};
}

function formatIsoAmount(t: Terminal, iso: string, minor: number, state: TimestopState): string {
  if (!minor) return '—';
  // Use the same formatter the Overview tab uses so the rendered
  // shape (e.g. "12,34zł" vs "12.34 zł") is identical.
  const cfg = getCurrencyByISO(t, iso, state);
  return formatDividedCurrency(minor, cfg);
}

// =====================================================================
// cardStatusIcons — rich icon strip replacing the old green/grey
// online dot. Two halves:
//
//   Left  (in display order):
//     ❄️  when set_recv.frozen == 1
//     ❌  when set_recv.force_sync != 1 (the terminal is NOT
//         forcing a server push, so the operator should assume
//         the live link is stale / dropped)
//     🪙 + 💵  when set_recv.mode == 1 (CASH: coins + banknotes)
//     🎮      when mode is 0 (FUN) or missing — no money taken
//
//   Right:
//     ⚙️  when set_send is non-empty (BO has staged settings
//         that haven't been ACK'd by the spot yet — gear means
//         "config pending")
//     🔧  when cmd_send is non-empty (BO has queued a command
//         — payout / reboot / etc — that the spot hasn't
//         picked up yet)
//
// The icons are wrapped in <span title="..."> for native
// browser tooltips. Color is set via Tailwind text-* classes
// so the BO can theme the strip in one place later if needed.
//
// mode encoding (the field the real cabinet actually reports;
// the old buy_mode/both/card tri-state is gone):
//   0  -> fun   (attract/demo, no money accepted)
//   1  -> cash  (coin + note)
// =====================================================================
function isNonEmpty(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'object') {
    if (Array.isArray(v)) return v.length > 0;
    return Object.keys(v as Record<string, unknown>).length > 0;
  }
  // strings / numbers: non-null counts as "present"
  return true;
}

export function cardStatusIcons(t: Terminal): string {
  const sr = (t.set_recv || {}) as { frozen?: number; force_sync?: number; mode?: number };
  const left: string[] = [];

  if (Number(sr.frozen) === 1) {
    left.push(`<span title="Frozen" class="text-blue-500 text-base leading-none">❄️</span>`);
  }
  if (Number(sr.force_sync) !== 1) {
    // No force_sync = the spot terminal doesn't push its
    // settings to the server proactively, so the link is
    // effectively read-only from the BO's side. Show a "no
    // connection" glyph so the operator doesn't think the
    // numbers are live.
    left.push(`<span title="No live connection" class="text-red-500 text-base leading-none">❌</span>`);
  }

  const buyMode = Number(sr.mode);
  // mode: 0 = fun (no money taken), 1 = cash (coin + note).
  // Anything else / missing falls back to the fun icon so a stale
  // card never advertises cash acceptance that isn't there.
  let showCoin = false, showNote = false, showFun = false;
  if (buyMode === 1) {            // cash
    showCoin = true; showNote = true;
  } else {                        // fun (0) or unknown
    showFun = true;
  }
  if (showFun) left.push(`<span title="Fun mode" class="text-base leading-none">🎮</span>`);
  if (showCoin) left.push(`<span title="Coin" class="text-base leading-none">🪙</span>`);
  // Banknote + card icons overlap the previous icon and are
  // tilted -30deg so the cluster reads as a small fanned stack
  // instead of a flat list. Coin stays upright because it
  // anchors the row.
  //   - Note always overlaps by 12px (always sits on top of coin
  //     in the cash/both clusters)
  //   - Card overlaps by 15px in the BOTH cluster (stacks on
  //     top of the note), but uses a normal 0 gap in the CARD-
  //     only cluster (it sits next to coin, no stacking)
  if (showNote) left.push(`<span title="Note" class="text-base leading-none inline-block" style="margin-left:-12px; transform: rotate(-30deg);">💵</span>`);

  const right: string[] = [];
  if (isNonEmpty(t.set_send)) {
    right.push(`<span title="Settings pending sync" class="text-amber-500 text-base leading-none">⚙️</span>`);
  }
  if (isNonEmpty(t.cmd_send)) {
    right.push(`<span title="Command pending" class="text-purple-500 text-base leading-none">🔧</span>`);
  }

  // Defensive fallback: if nothing at all applies, show a
  // single light dash so the row stays a fixed height across
  // cards and the layout doesn't jitter between them.
  const leftHtml = left.length > 0 ? left.join('') : '<span class="text-gray-300">—</span>';
  const rightHtml = right.length > 0 ? right.join('') : '';

  return `<div class="flex items-center justify-between w-full">`
    + `<div class="flex items-center gap-x-2">${leftHtml}</div>`
    + `<div class="flex items-center gap-x-2">${rightHtml}</div>`
    + `</div>`;
}

// Games played per game type (3 slots) from acc_long.games.count. The
// cabinet reports three game counters; the old per-hopper token bucket
// and the card channel no longer exist.
export function gameCountsFor(t: Terminal): number[] {
  const acc = t.acc_long as { games?: { count?: unknown } } | undefined;
  const raw = acc?.games?.count;
  const out = [0, 0, 0];
  if (Array.isArray(raw)) {
    for (let i = 0; i < 3; i++) out[i] = Number(raw[i] || 0);
  }
  return out;
}

function gamesPlayed(t: Terminal): number {
  return gameCountsFor(t).reduce((a, b) => a + b, 0);
}

export function renderAccBlocks(t: Terminal, state: TimestopState): string {
  const isos = isoList(t);
  // Empty terminal (no currencies) -- render zeros placeholders so
  // the card layout doesn't collapse.
  // CASH = coins + notes summed per ISO.
  const cashCells = (): string => {
    if (!isos.length) return '<div class="text-gray-300 text-xs">—</div>';
    const coin = moneyInFor(t, 'coin');
    const note = moneyInFor(t, 'note');
    return isos.map(iso => {
      const total = Number(coin[iso] || 0) + Number(note[iso] || 0);
      return `<div style="min-width:0;"><div class="${ACC_LABEL}">${escapeHtml(iso)}</div><div class="truncate">${escapeHtml(formatIsoAmount(t, iso, total, state))}</div></div>`;
    }).join('');
  };

  // GAMES -- 3 cells, games played per game type (acc_long.games.count).
  const gameCells = gameCountsFor(t).map((n, i) =>
    `<div><div class="${ACC_LABEL}">#${i + 1}</div><div>${Number(n).toLocaleString()}</div></div>`
  ).join('');

  // Small "since: {date}" annotation above the CASH block. Pulled
  // from acc_long.st (the spot terminal's local-time snapshot of
  // when the long period started). Trimmed to MM-DD so the line
  // stays compact and matches the rest of the card's date style
  // (Year is implied — it's the long period, so always "this run").
  // Falls back to "—" when the field is missing, which keeps the
  // layout consistent across terminals that have not yet posted
  // any accounting data.
  const stRaw = (t.acc_long && typeof t.acc_long.st === 'string') ? t.acc_long.st : '';
  const stShort = stRaw ? stRaw.slice(5, 10) : '—';
  const sinceAnnot = `<div class="text-[10px] text-gray-400 text-right -mb-1">since: ${escapeHtml(stShort)}</div>`;

  return `
    ${sinceAnnot}
    <div class="${ACC_SECTION_BOX}">
      <div class="${ACC_LABEL}">Cash</div><hr>
      <div style="${isoGridStyle(isos.length)}">${cashCells()}</div>
    </div>
    <div class="${ACC_SECTION_BOX}">
      <div class="${ACC_LABEL}">${escapeHtml(i18n('common.games'))}</div><hr>
      <div style="${isoGridStyle(3)}">${gameCells}</div>
    </div>
    ${renderVelocityBlock(t, state)}`;
}

// =====================================================================
// renderVelocityBlock -- TODAY / AVG / MONTHLY row, 3 columns.
//   TODAY   = sum of 3 hopper counts for today's date, sourced from
//             /api/terminalacc (per-day acc_diff). Async; the cell
//             is rendered with a "—" placeholder and patched in
//             after the fetch resolves (see fetchTodayAccDiff).
//   AVG     = (acc_long.out[0..2].cnt sum) / days since acc_long.st.
//             Days = (now - st) in fractional days, floored at
//             ~1/1000 to keep the division safe when the period
//             just started (st in the last few seconds). Falls
//             back to "—" if st is missing/unparseable.
//   MONTHLY = round(AVG * 30.3). 30.3 is the average month length
//             in days (365/12 = 30.4166, rounded down to 30.3 in
//             the spec).
//
// The cell ids are stable per (terminal id, row key) so re-renders
// find the same node to patch.
// =====================================================================

function daysSince(st: unknown, now: number): number | null {
  if (typeof st !== 'string' || !st) return null;
  // acc_long.st is "YYYY-MM-DD HH:MM:SS" (server local). Treat
  // it as a naive local time -- the spot terminal also stores
  // the st as the local-time snapshot of when the long period
  // started, so subtracting "now" against a local Date gives
  // the correct interval from the operator's POV.
  const cleaned = st.replace(' ', 'T');
  const ms = Date.parse(cleaned);
  if (isNaN(ms)) return null;
  const diffMs = now - ms;
  if (diffMs <= 0) return null;
  return diffMs / (1000 * 60 * 60 * 24);
}

function totalGamesCnt(t: Terminal): number {
  return gamesPlayed(t);
}

function renderVelocityBlock(t: Terminal, state: TimestopState): string {
  const idSafe = String(t.id).replace(/[^a-zA-Z0-9_-]/g, '_');
  const cellId = (key: string): string => `vt-${idSafe}-${key}`;

  // ---- TODAY: placeholder until /api/terminalacc resolves ----
  const todayPlaceholder = `<span class="text-gray-300">—</span>`;
  const todayCell = `<div id="${cellId('today')}" class="font-semibold">${todayPlaceholder}</div>`;

  // ---- AVG / MONTHLY: synchronous (everything is on t.acc_long) ----
  const totalCnt = totalGamesCnt(t);
  const now = Date.now();
  const days = daysSince(t.acc_long && t.acc_long.st, now);
  let avg: number | null = null;
  let monthly: number | null = null;
  if (days !== null) {
    avg = totalCnt / days;
    monthly = Math.round(avg * 30.3);
  }
  const avgCell = `<div class="font-semibold">${avg === null
    ? `<span class="text-gray-300">—</span>`
    : escapeHtml(avg.toFixed(2))}</div>`;
  const monthlyCell = `<div class="font-semibold">${monthly === null
    ? `<span class="text-gray-300">—</span>`
    : escapeHtml(monthly.toLocaleString())}</div>`;

  return `
    <div class="${ACC_SECTION_BOX}">
      <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0.5rem;align-items:start;">
        <div><div class="${ACC_LABEL}">Today</div>${todayCell}</div>
        <div><div class="${ACC_LABEL}">Avg / day</div>${avgCell}</div>
        <div><div class="${ACC_LABEL}">Monthly</div>${monthlyCell}</div>
      </div>
    </div>`;
}

// Fetch today's per-day acc_diff for the given terminal, then sum the
// 3 hopper cnts across any record dated today (a day can have >1
// record after multiple resets) and patch the TODAY cell in the DOM.
// Fire-and-forget: a 404 / network error keeps the placeholder
// instead of raising. Idempotent — running it twice on the same
// terminal just re-paints the same number.
export async function fetchTodayAccDiff(
  t: Terminal,
  state: TimestopState,
  config: ApiFetchConfig,
): Promise<void> {
  const idSafe = String(t.id).replace(/[^a-zA-Z0-9_-]/g, '_');
  const cellId = `vt-${idSafe}-today`;
  try {
    // Build today's YYYY-MM-DD using local time (matches the
    // acc_diff's `dated` column, which the spot terminal writes in
    // its own local TZ).
    const now = new Date();
    const pad = (n: number): string => (n < 10 ? '0' : '') + n;
    const todayYmd = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

    const r = await apiFetch(
      'terminalacc?id=' + encodeURIComponent(String(t.id))
        + '&from=' + encodeURIComponent(todayYmd)
        + '&to='   + encodeURIComponent(todayYmd),
      { method: 'GET' },
      config,
    );
    if (!r || !r.ok || !r.data || typeof r.data !== 'object') return;
    const records = ((r.data as { records?: unknown[] }).records) || [];
    let cnt = 0;
    for (const rec of records) {
      if (!rec || typeof rec !== 'object') continue;
      const r2 = rec as { dated?: string; acc_diff?: unknown };
      if (r2.dated !== todayYmd) continue;
      const acc = r2.acc_diff as { games?: { count?: unknown } } | null;
      const slots = acc?.games?.count;
      if (!Array.isArray(slots)) continue;
      for (const n of slots) cnt += Number(n || 0);
    }
    const el = document.getElementById(cellId);
    if (el) {
      el.textContent = cnt.toLocaleString();
      el.classList.remove('text-gray-300');
    }
  } catch {
    // Keep the placeholder.
  }
}

// =====================================================================
// renderGroupAggregateBlock — group-level CASH / CASHLESS / HOPPERS OUT
// / TODAY / AVG / MONTHLY rolled up across every terminal in a given
// bucket. The visual shape mirrors the per-terminal renderAccBlocks +
// renderVelocityBlock so a group header reads the same as the cards
// hanging under it, but the ISO grid is the UNION of every terminal's
// cur_recv (a group can accept CZK on one terminal and EUR on another,
// and the operator wants to see both columns).
//
// This is purely synchronous — every cell except TODAY is sourced from
// the same acc_long block the per-terminal renderer uses. TODAY is
// async (see fetchGroupTodayAccDiff) and gets a placeholder that the
// patcher replaces once the parallel per-terminal fetches resolve.
//
// The cell ids are stable per (scope, bucket key, row key) so
// re-renders find the same node to patch:
//   gt-list-2-today, gt-grp-2-today, gt-list-__unassigned__-today, ...
// `bucket` is the Result of groupTerminalsByOid(state). `state` is
// forwarded so getCurrencyByISO can honour the DB-backed currency
// defaults for any ISO the terminals don't declare in their own
// cur_recv. `scope` is 'list' for the Quick Terminals / All
// Terminals page aggregate and 'groups' for the Groups page card
// aggregate. The two scopes tag the same logical bucket so the
// cell-ids don't collide when both views are mounted in the DOM at
// the same time (the bundle keeps every page's container in the DOM
// and toggles hidden classes on navigate).
// =====================================================================

function safeBucketKey(key: string): string {
  return String(key).replace(/[^a-zA-Z0-9_-]/g, '_');
}

type GroupAggScope = 'list' | 'groups';

// Cell-id scheme:
//   list-page (Quick Terminals / All Terminals): gt-list-<bucketKey>-<key>
//   groups-page (Groups page):                  gt-grp-<bucketKey>-<key>
// The two scopes tag the same logical bucket so the cell-ids don't
// collide when both views are mounted in the DOM at the same time
// (the bundle keeps every page's container in the DOM and toggles
// hidden classes on navigate). Without the scope prefix the
// today's-fetch patch would race against the wrong container.
function groupCellId(scope: GroupAggScope, bucketKey: string, key: string): string {
  return `gt-${safeBucketKey(scope)}-${safeBucketKey(bucketKey)}-${key}`;
}

export function renderGroupAggregateBlock(
  bucket: Bucket,
  state: TimestopState,
  scope: GroupAggScope = 'list',
): string {
  const cellId = (key: string): string => groupCellId(scope, bucket.key, key);

  // Union of every ISO the bucket's terminals have declared in
  // cur_recv. Walked in first-seen order so the columns come out
  // stable across re-renders; using a Set first to dedupe.
  const isoSet = new Set<string>();
  for (const t of bucket.terminals) {
    for (const cfg of Object.values(t.cur_recv || {})) {
      if (cfg && cfg.ISO) isoSet.add(String(cfg.ISO));
    }
  }
  const isos = Array.from(isoSet);

  // Pick a representative terminal for the per-ISO format lookup --
  // getCurrencyByISO needs a Terminal row to thread through (the
  // DB-backed defaults live in state.currencyDefaults keyed by ISO,
  // but the helper only looks up via the terminal's own cur_recv).
  // Any terminal that knows the ISO works; the helper falls back to
  // state.currencyDefaults when the terminal row is missing the ISO.
  const sampleFor = (iso: string): Terminal | null => {
    for (const t of bucket.terminals) {
      for (const cfg of Object.values(t.cur_recv || {})) {
        if (cfg && String(cfg.ISO) === iso) return t;
      }
    }
    return bucket.terminals[0] || null;
  };

  // Sum money-in per ISO across the bucket. CASH = coin + note summed
  // together (matches the per-terminal card's "Cash" row).
  const sums = (kind: 'coin' | 'note' | 'card'): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const t of bucket.terminals) {
      const slot = moneyInFor(t, kind);
      for (const iso of Object.keys(slot)) {
        out[iso] = (out[iso] || 0) + Number(slot[iso] || 0);
      }
    }
    return out;
  };

  // Hoppers OUT — every terminal contributes 3 hopper slots; we sum
  // each slot index across the bucket so the resulting row reads as
  // "the bucket paid out N tokens from hopper #1, M from #2, ..."
  // (sum of identical indices, NOT a global total — the per-terminal
  // card shows the same 3-cell layout, so we keep the cell structure).
  const gameSums = [0, 0, 0];
  let totalGames = 0;
  for (const t of bucket.terminals) {
    const counts = gameCountsFor(t);
    for (let i = 0; i < 3; i++) {
      gameSums[i] += counts[i];
      totalGames += counts[i];
    }
  }

  // ---- AVG / MONTHLY — synchronous from acc_long sums ----
  // AVG = games played / days_since_oldest_period_start. We use the
  // OLDEST st across the bucket's terminals because that's the
  // longest-running period that contributes to the bucket's "today"
  // — averaging across a shorter window would inflate the rate.
  // If every terminal has no st, fall back to "—".
  const now = Date.now();
  let earliestMs: number | null = null;
  for (const t of bucket.terminals) {
    const stRaw = t.acc_long && typeof t.acc_long.st === 'string' ? t.acc_long.st : '';
    if (!stRaw) continue;
    const ms = Date.parse(stRaw.replace(' ', 'T'));
    if (isNaN(ms)) continue;
    if (earliestMs === null || ms < earliestMs) earliestMs = ms;
  }
  let avg: number | null = null;
  let monthly: number | null = null;
  if (earliestMs !== null) {
    const days = (now - earliestMs) / (1000 * 60 * 60 * 24);
    if (days > 0) {
      avg = totalGames / days;
      monthly = Math.round(avg * 30.3);
    }
  }

  // ---- TODAY ----
  // Stays empty until fetchGroupTodayAccDiff patches in the sum. The
  // cell id is stable so the patcher finds it.
  const todayCell = `<div id="${cellId('today')}" class="font-semibold"><span class="text-gray-300">—</span></div>`;
  const avgCell = `<div class="font-semibold">${avg === null
    ? `<span class="text-gray-300">—</span>`
    : escapeHtml(avg.toFixed(2))}</div>`;
  const monthlyCell = `<div class="font-semibold">${monthly === null
    ? `<span class="text-gray-300">—</span>`
    : escapeHtml(monthly.toLocaleString())}</div>`;

  // ---- ISO cell renderers ----
  const fmtSum = (sumsMap: Record<string, number>): string => {
    if (!isos.length) return '<div class="text-gray-300 text-xs">—</div>';
    return isos.map(iso => {
      const v = sumsMap[iso] || 0;
      const sample = sampleFor(iso);
      const text = v
        ? (sample ? formatIsoAmount(sample, iso, v, state) : String(v))
        : '—';
      return `<div style="min-width:0;"><div class="${ACC_LABEL}">${escapeHtml(iso)}</div><div class="truncate">${escapeHtml(text)}</div></div>`;
    }).join('');
  };
  const fmtGames = (): string =>
    gameSums.map((n, i) => `<div><div class="${ACC_LABEL}">#${i + 1}</div><div>${n.toLocaleString()}</div></div>`).join('');

  const coin = sums('coin');
  const note = sums('note');
  const cashMerge: Record<string, number> = {};
  for (const iso of isos) cashMerge[iso] = (coin[iso] || 0) + (note[iso] || 0);

  // Bucket label: call out the bucket on the cash header so the
  // operator sees which row they're reading. The Today/Avg/Monthly
  // cells use the same labels the per-terminal card uses so they
  // map 1:1 to the cards hanging under the bucket.
  return `
    <div class="grid grid-cols-1 gap-2">
      <div class="${ACC_SECTION_BOX}">
        <div class="${ACC_LABEL}">${escapeHtml(i18n('common.cash'))}</div><hr>
        <div style="${isoGridStyle(isos.length)}">${fmtSum(cashMerge)}</div>
      </div>
      <div class="${ACC_SECTION_BOX}">
        <div class="${ACC_LABEL}">${escapeHtml(i18n('common.games'))}</div><hr>
        <div style="${isoGridStyle(3)}">${fmtGames()}</div>
      </div>
      <div class="${ACC_SECTION_BOX}">
        <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0.5rem;align-items:start;">
          <div><div class="${ACC_LABEL}">${escapeHtml(i18n('common.today'))}</div>${todayCell}</div>
          <div><div class="${ACC_LABEL}">${escapeHtml(i18n('common.avg_per_day'))}</div>${avgCell}</div>
          <div><div class="${ACC_LABEL}">${escapeHtml(i18n('common.monthly'))}</div>${monthlyCell}</div>
        </div>
      </div>
    </div>`;
}

// Fetch today's per-day acc_diff for every terminal in the bucket,
// sum the 3 hopper cnts across any record dated today, and patch the
// bucket's TODAY cell. Fire-and-forget — a network error keeps the
// placeholder. Idempotent across re-renders (the cell id is fixed).
// The `scope` parameter MUST match the one used by the caller that
// ran renderGroupAggregateBlock; otherwise the patcher resolves a
// different element than the renderer painted.
//
// One /api/terminalacc per terminal (Promise.all). For a bucket of
// 11 terminals that's 11 parallel GETs — the existing per-terminal
// fetcher does the same thing, so the server load shape doesn't
// change with this addition.
export async function fetchGroupTodayAccDiff(
  bucket: Bucket,
  state: TimestopState,
  config: ApiFetchConfig,
  scope: GroupAggScope = 'list',
): Promise<void> {
  const cellId = groupCellId(scope, bucket.key, 'today');
  if (!bucket.terminals.length) return;
  try {
    const now = new Date();
    const pad = (n: number): string => (n < 10 ? '0' : '') + n;
    const todayYmd = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

    const results = await Promise.all(bucket.terminals.map(async (t) => {
      const r = await apiFetch(
        'terminalacc?id=' + encodeURIComponent(String(t.id))
          + '&from=' + encodeURIComponent(todayYmd)
          + '&to='   + encodeURIComponent(todayYmd),
        { method: 'GET' },
        config,
      );
      if (!r || !r.ok || !r.data || typeof r.data !== 'object') return 0;
      const records = ((r.data as { records?: unknown[] }).records) || [];
      let cnt = 0;
      for (const rec of records) {
        if (!rec || typeof rec !== 'object') continue;
        const r2 = rec as { dated?: string; acc_diff?: unknown };
        if (r2.dated !== todayYmd) continue;
        const acc = r2.acc_diff as { games?: { count?: unknown } } | null;
        const slots = acc?.games?.count;
        if (!Array.isArray(slots)) continue;
        for (const n of slots) cnt += Number(n || 0);
      }
      return cnt;
    }));

    let total = 0;
    for (const n of results) total += n;
    // Multiple views (Quick Terminals / All Terminals / Groups)
    // can render the same logical bucket with the same cell id at
    // once; the bundle keeps every page's container in the DOM
    // and toggles hidden classes on navigate. Patch every cell
    // bearing this id so the active view always shows the fresh
    // number, never the placeholder dash.
    const els = document.querySelectorAll(`#${cellId}`);
    els.forEach(el => {
      el.textContent = total.toLocaleString();
      el.classList.remove('text-gray-300');
    });
  } catch {
    // Keep the placeholder.
  }
}

// =====================================================================
// renderKPIs — the four dashboard KPI cards
// =====================================================================

export function renderKPIs(state: TimestopState): void {
  const total   = Object.keys(state.terminals).length;
  const online  = Object.values(state.terminals).filter(t => (t.onlineAgo ?? Infinity) < 40_000).length;
  const offline = total - online;
  const onlinePct = total > 0 ? Math.round((online / total) * 100) : 0;

  const elOnline = document.getElementById('kpi-online');
  const elOnlineSub = document.getElementById('kpi-online-sub');
  if (elOnline) elOnline.textContent = `${online} / ${total}`;
  if (elOnlineSub) {
    elOnlineSub.innerHTML = offline > 0
      ? `<span class="text-amber-600">●</span> ${i18n('dashboard.kpi_offline_sub', { count: offline, pct: onlinePct })}`
      : `<span class="text-emerald-600">●</span> ${i18n('dashboard.kpi_all_online')}`;
  }

  // Games played (long period) + revenue
  let totalGames = 0;
  let totalRevenue = 0;          // minor units
  const currencyCounts: Record<string, number> = {};

  for (const t of Object.values(state.terminals)) {
    const acc = t.acc_long || {};
    const moneyIn = (acc as { in?: Record<string, Record<string, number> | null | undefined> }).in || {};
    // Games played = the three game counters the cabinet reports
    // (games.count[0..2]); the old per-hopper `out` bucket is gone.
    const gameCounts = (acc as { games?: { count?: unknown } }).games?.count;
    if (Array.isArray(gameCounts)) {
      for (const n of gameCounts) totalGames += Number(n || 0);
    }

    for (const payType of ['coin', 'note']) {
      const m = moneyIn[payType] || {};
      for (const iso of Object.keys(m)) {
        const raw = Number(m[iso] || 0);
        if (!raw) continue;
        let div = 1;
        const cur = t.cur_recv && Object.values(t.cur_recv).find(c => c.ISO === iso);
        if (cur && cur.div) div = Number(cur.div);
        totalRevenue += raw;
        currencyCounts[iso] = (currencyCounts[iso] || 0) + raw;
      }
    }
  }

  const dominantIso = Object.keys(currencyCounts)
    .sort((a, b) => currencyCounts[b] - currencyCounts[a])[0] || '';
  let dominantDiv = 1;
  const firstTerminal = Object.values(state.terminals)[0];
  if (dominantIso && firstTerminal && firstTerminal.cur_recv) {
    const cur = Object.values(firstTerminal.cur_recv).find(c => c.ISO === dominantIso);
    if (cur && cur.div) dominantDiv = Number(cur.div);
  }
  const el = Object.values(firstTerminal?.cur_recv || {}).find(c => c?.ISO === dominantIso);
  const revenueDisplay = dominantIso
    ? formatDividedCurrency(totalRevenue, el ?? null)
    : '0';
  const isoCount = Object.keys(currencyCounts).length;

  const elSold = document.getElementById('kpi-sold');
  const elSoldSub = document.getElementById('kpi-sold-sub');
  if (elSold) elSold.textContent = totalGames.toLocaleString();
  if (elSoldSub) elSoldSub.textContent = `Across ${total} terminal${total !== 1 ? 's' : ''}`;

  const elRev = document.getElementById('kpi-revenue');
  const elRevSub = document.getElementById('kpi-revenue-sub');
  if (elRev) elRev.textContent = revenueDisplay + (dominantIso ? ` ${dominantIso}` : '');
  if (elRevSub) {
    const avg = totalGames > 0 ? (totalRevenue / dominantDiv / totalGames) : 0;
    elRevSub.textContent = `Avg ${avg.toFixed(2)}${dominantIso ? ` ${dominantIso}` : ''} per game${isoCount > 1 ? ` • ${isoCount} currencies` : ''}`;
  }

  // Top terminal
  let topTerminal: Terminal | null = null;
  let topScore = -1;
  for (const t of Object.values(state.terminals)) {
    const acc = t.acc_long || {};
    const gameCounts = Array.isArray((acc as unknown as { games?: { count?: unknown } }).games?.count)
      ? ((acc as unknown as { games: { count: unknown[] } }).games.count as unknown[])
      : [];
    let score = 0;
    for (const n of gameCounts as number[]) score += Number(n || 0);
    if (score > topScore) {
      topScore = score;
      topTerminal = t;
    }
  }
  const elTopName = document.getElementById('kpi-top-name');
  const elTopSub = document.getElementById('kpi-top-sub');
  if (topTerminal && topScore > 0) {
    if (elTopName) elTopName.textContent = topTerminal.name || `#${topTerminal.id}`;
    if (elTopSub) elTopSub.textContent = `${topTerminal.location || topTerminal.mid || ''} • ${topScore} games`;
  } else {
    if (elTopName) elTopName.textContent = '—';
    if (elTopSub) elTopSub.textContent = i18n('terminal_list.no_sales_yet');
  }
}

// =====================================================================
// renderQuickTerminals — dashboard strip (4 columns)
// =====================================================================

export function renderQuickTerminals(state: TimestopState): void {
  const container = document.getElementById('quick-terminals');
  if (!container) return;

  if (!state.terminals || Object.keys(state.terminals).length === 0) {
    container.innerHTML = '<div class="col-span-full bg-white rounded-3xl p-8 text-center text-gray-400 border">No terminals yet.</div>';
    return;
  }

  // ---- Caps (per dashboard-it-2026-07-28: unassigned 11, assigned 7) ----
  // The dashboard strip is a glance view, not a full list. Capping
  // each side keeps the strip readable while still giving the
  // operator a meaningful sample of what is online. "+N more" pills
  // make the truncation visible so the dashboard never silently
  // hides terminals — the operator can flip to the All Terminals
  // page for the unfiltered view.
  const UNASSIGNED_CAP = 11;
  const ASSIGNED_CAP = 7;

  // "Recent" = last-seen timestamp. ts_seen is the spot terminal's
  // last poll time (server local); terminals without a ts_seen fall
  // to the bottom of the recency sort (Infinity). We use a Date
  // parse here so terminals that have never polled (no ts_seen) are
  // still listed but always sorted below the ones that have.
  const recencyMs = (t: Terminal): number => {
    const raw = (t as { ts_seen?: unknown }).ts_seen;
    if (typeof raw !== 'string' || !raw) return Number.POSITIVE_INFINITY;
    const ms = Date.parse(raw.replace(' ', 'T'));
    return isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
  };
  // Most-recent first.
  const byRecencyDesc = (a: Terminal, b: Terminal): number => recencyMs(b) - recencyMs(a);

  const buckets = groupTerminalsByOid(state);
  // Split into unassigned + assigned and sort by recency.
  const unassigned = buckets.find(b => b.key === '__unassigned__');
  const assignedBuckets = buckets.filter(b => b.key !== '__unassigned__');
  const assignedSorted: Terminal[] = assignedBuckets
    .flatMap(b => b.terminals)
    .slice()  // copy — don't mutate the bucket-derived arrays
    .sort(byRecencyDesc);
  const unassignedSorted: Terminal[] = unassigned
    ? unassigned.terminals.slice().sort(byRecencyDesc)
    : [];

  // cardHtml -- the Quick view card body. When the card belongs
  // to the unassigned bucket, the dashboard dims it (opacity 0.6)
  // so the operator can tell at a glance that those terminals are
  // unaccounted for. The other dashboard chrome (status pill,
  // location, mid, status-icons strip) stays visible so the card
  // is still actionable -- clicking it still opens the terminal
  // modal.
  const cardHtml = (t: Terminal, isUnassigned: boolean): string => {
    const online = t.status === 'Online';
    const icon = displayTerminalIcon(t);
    const name = displayTerminalName(t);
    const agoMs = (t as { onlineAgo?: number }).onlineAgo;
    const agoLabel = online ? ' - ' : (agoMs != null ? formatDuration(agoMs) : '');
    // (Phase 7: 2-bucket bg scheme that matches the inline index.php
    // renderQuickTerminals. The bundle's previous 5-bucket scheme
    // (lime/green/teal/sky/gray) had a dead branch — < 2h checked
    // before < 1h — and shipped a different visual. Reverted to the
    // inline colours so the bundle is behaviour-preserving. The
    // debug `bg-<onlineAgo>` suffix is also dropped since the inline
    // ships it only as a debug aid and it pollutes the DOM with
    // Tailwind-unsafe class names.)
    const bgstyle = agoMs != null && agoMs < 45_000
      ? 'bg-emerald-200'
      : agoMs != null && agoMs < 545_000
        ? 'bg-yellow-100'
        : 'bg-white';
    const testingClass = Number(t.runtime) === 0 ? ' testing' : '';
    const dimClass = isUnassigned ? ' opacity-60' : '';
    return `<div onclick="openTerminal(${t.id})" class="${bgstyle}${testingClass}${dimClass} rounded-3xl p-5 card-hover cursor-pointer flex flex-col gap-y-1">
      <div class="flex justify-between items-start gap-x-2">
        <div class="flex items-center gap-x-2 min-w-0">
          <span class="text-lg flex-shrink-0">${escapeHtml(icon)}</span>
          <h4 class="font-semibold text-lg truncate" title="${escapeHtml(name)}">${escapeHtml(name)}</h4>
        </div>
        <span class="text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-x-1.5 flex-shrink-0 ${online ? 'bg-emerald-300 text-emerald-800' : 'bg-gray-100 text-gray-500'}">
          <span class="inline-block w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}"></span>
          ${escapeHtml(agoLabel)}
        </span>
      </div>
      <div class="flex items-center gap-x-2">
        <span class="text-gray-400 text-sm flex-shrink-0">📍</span>
        <p class="text-gray-500 text-sm truncate">${escapeHtml(t.location || '—')}</p>
      </div>
      <p class="text-gray-400 text-xs font-mono truncate">${escapeHtml(t.mid != null ? String(t.mid) : '—')}</p>
      <div class="mt-2 pt-2 border-t border-gray-100 flex items-center gap-x-2 text-sm">${cardStatusIcons(t)}</div>
    </div>`;
  };

  // helper: render a heading + capped card list. The "+N more" pill
  // is appended to the count suffix only when the cap truncates.
  const renderSection = (label: string, terminals: Terminal[], cap: number, isUnassigned: boolean): string => {
    if (terminals.length === 0) return '';
    const shown = terminals.slice(0, cap);
    const hidden = terminals.length - shown.length;
    const cards = shown.map(t => cardHtml(t, isUnassigned)).join('');
    const morePill = hidden > 0
      ? `<span class="ml-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title="${hidden} more terminal${hidden === 1 ? '' : 's'} not shown on the dashboard — open the All Terminals page for the full list">+${hidden} more</span>`
      : '';
    const heading = `<div class="col-span-full flex items-baseline justify-between mt-2 mb-1">
      <h3 class="text-sm uppercase tracking-widest text-gray-300 font-semibold">${escapeHtml(label)}</h3>
      <span class="text-xs text-gray-400">${i18n('dashboard.terminals_suffix', { count: terminals.length })}${morePill}</span>
    </div>`;
    return heading + cards;
  };

  const sections: string[] = [];
  // Unassigned first (existing convention).
  sections.push(renderSection(i18n('terminal_list.group_unassigned'), unassignedSorted, UNASSIGNED_CAP, true));
  // "Other terminals" = assigned ones, sorted by recency.
  if (assignedSorted.length > 0) {
    sections.push(renderSection(i18n('dashboard.other_terminals'), assignedSorted, ASSIGNED_CAP, false));
  }

  container.innerHTML = sections.join('');
}

// =====================================================================
// All Terminals page.
// =====================================================================

// renderAccBlocksLegend — the icon legend rendered once at the top
// of the All Terminals page so the operator doesn't have to guess
// what the snowflake / no-connection / overlapping coin / gear /
// wrench icons on each card mean. Each entry pairs the same
// glyph the card bottom row uses with a short caption.
//
// The "Coin + Banknote" pair is shown as a single "Cash" label
// with the two glyphs visually overlapping (margin-left:-12px
// on the banknote, the same trick cardStatusIcons() uses) so the
// legend matches the actual fanned cluster the user sees on
// each card. Showing them as two separate legend items would
// mislead — the cards always render them stacked.
//
// The legend also includes two small rounded sample tiles that
// mirror the actual card-state CSS classes (.testing and
// .frozen) so the operator can see what the visual treatment
// looks like in context — not just the icon, but the border /
// shadow style applied to a Testing or Frozen card.
//
// Layout is a 1-row flex with a small gap so it stays compact
// on mobile and desktop alike.
function renderAccBlocksLegend(): string {
  // Cash pair: coin upright, then banknote tilted -30deg
  // overlapping by 12px, mirroring cardStatusIcons(). The
  // rotation is preserved on the legend so the operator can
  // recognise the cluster at a glance without re-learning the
  // glyphs.
  const cashCluster = `
    <span class="inline-flex items-center" title="Buy mode includes coins and banknotes">
      <span class="text-base leading-none">🪙</span>
      <span class="text-base leading-none inline-block" style="margin-left:-12px; transform: rotate(-30deg);">💵</span>
    </span>
    <span class="text-sm text-gray-600">Cash</span>`;
  // Card: same as the cards' card icon — tilted -30deg, but
  // since this legend has no coin to its left, the card sits
  // at its natural position (no -15px overlap, which only
  // applies in the "both" cluster on the cards themselves).
  const cardIcon = `
    <span class="text-base leading-none inline-block" style="transform: rotate(-30deg);">💳</span>`;
  const items: Array<{ glyph: string; label: string; title: string }> = [
    { glyph: '❄️', label: 'Frozen',          title: 'Terminal is in the frozen state (set_recv.frozen=1)' },
    { glyph: '❌', label: 'No sync',         title: 'Terminal has not enabled force_sync — no live connection' },
  ];
  const cells = items.map(i => `
    <span class="inline-flex items-center gap-x-1.5" title="${escapeHtml(i.title)}">
      <span class="text-base leading-none">${i.glyph}</span>
      <span class="text-sm text-gray-600">${escapeHtml(i.label)}</span>
    </span>`).join('');
  // Three mini sample tiles that mirror the real card-state CSS
  // classes:
  //   - bg-emerald-200 = online (Quick view + All Terminals card
  //     background when the terminal pinged < 45s ago)
  //   - .testing        = dashed-orange left/right border
  //                       (Phase 12.18f; applied when mach.runtime=0)
  //   - .frozen-mini    = inset blue glow sized for a small
  //                       legend tile (admin/index.php line 185;
  //                       mirrors the full .frozen rule but with
  //                       a smaller spread so the effect is
  //                       visible on a 40x28px swatch)
  // The full .frozen rule (0 -4px 25px 11px #06c inset) is too
  // large for a w-10 h-7 legend tile, so the legend uses
  // .frozen-mini instead. The actual cards still get the full
  // effect via .frozen.
  //
  // Each tile is w-10 h-7 (40x28px) so the bg / border / shadow
  // effect is obvious at a glance without dominating the
  // legend. They sit at the start of the legend (before the
  // icon glyphs) so the operator reads the visual treatments
  // first, then the icon meanings.
  const styleSamples = `
    <span class="inline-flex items-center gap-x-1.5" title="Online — terminal pinged within the last 45 seconds (Quick view / All Terminals card background)">
      <span class="bg-emerald-200 inline-block w-10 h-7 rounded-md border border-gray-200"></span>
      <span class="text-sm text-gray-600">Online</span>
    </span>
    <span class="inline-flex items-center gap-x-1.5" title="Terminal is in the lab/staging phase (mach.runtime=0)">
      <span class="testing inline-block w-10 h-7 rounded-md border border-gray-200"></span>
      <span class="text-sm text-gray-600">Testing</span>
    </span>
    <span class="inline-flex items-center gap-x-1.5" title="Terminal is in the frozen state (set_recv.frozen=1)">
      <span class="frozen-mini inline-block w-10 h-7 rounded-md border border-gray-200"></span>
      <span class="text-sm text-gray-600">Frozen</span>
    </span>`;
  return `
    <div class="col-span-full bg-white rounded-3xl px-5 py-3 mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 border border-gray-200">
      <span class="text-xs uppercase tracking-widest text-gray-400 font-semibold mr-2">Icon legend</span>
      ${styleSamples}
      <span class="w-px h-5 bg-gray-200" aria-hidden="true"></span>
      ${cells}
      <span class="inline-flex items-center gap-x-1.5" title="Buy mode includes coins and banknotes">
        ${cashCluster}
      </span>
      <span class="inline-flex items-center gap-x-1.5" title="Buy mode includes card payments">
        ${cardIcon}
        <span class="text-sm text-gray-600">Card</span>
      </span>
      <span class="inline-flex items-center gap-x-1.5" title="Unsynced set_send payload waiting for the terminal to poll">
        <span class="text-base leading-none">⚙️</span>
        <span class="text-sm text-gray-600">Settings pending</span>
      </span>
      <span class="inline-flex items-center gap-x-1.5" title="Unsynced cmd_send payload waiting for the terminal to poll">
        <span class="text-base leading-none">🔧</span>
        <span class="text-sm text-gray-600">Command pending</span>
      </span>
    </div>`;
}

export function renderAllTerminals(state: TimestopState): void {
  const container = document.getElementById('all-terminals');
  if (!container) return;
  if (!state.terminals || Object.keys(state.terminals).length === 0) {
    container.innerHTML = '<div class="col-span-full bg-white rounded-3xl p-8 text-center text-gray-400 border">No terminals yet. Click "Add new terminal" to create one.</div>';
    return;
  }

  const buckets = groupTerminalsByOid(state);
  // Flatten the buckets into the actual list of terminals that get a
  // card rendered. After the innerHTML is set, we fire
  // fetchTodayAccDiff() for each so the new TODAY/AVG/MONTHLY row
  // can patch its placeholder with the real per-day total. We
  // do this AFTER innerHTML because the placeholders need to be
  // in the DOM for getElementById to find them.
  const renderedTerminals: Terminal[] = [];
  const allCardsHtml = buckets
    .filter(b => b.terminals.length > 0)
    .map(b => {
      const cards = b.terminals.map(t => {
        renderedTerminals.push(t);
        const online = t.status === 'online';
        const icon = displayTerminalIcon(t);
        const name = displayTerminalName(t);
        const agoMs = (t as { onlineAgo?: number }).onlineAgo;
        const agoLabel = online ? String(t.status || '') : (agoMs != null ? formatDuration(agoMs) : '');
        // Copy Quick view's 2-bucket bg scheme so the two views are
        // visually consistent: a card with a recent ping wears the
        // same color tint the dashboard's strip gives it. Thresholds
        // match renderQuickTerminals: < 45s = online, < ~9m = recent,
        // otherwise white. Falls back to white when onlineAgo is
        // missing (e.g. terminals that have never pinged).
        const cardBg = agoMs != null && agoMs < 45_000
          ? 'bg-emerald-200'
          : agoMs != null && agoMs < 545_000
            ? 'bg-yellow-100'
            : 'bg-white';
        // The `frozen` class is a hook for downstream styling
        // (e.g. dim the card, add a border accent) — the BO
        // owner requested it so a future theme pass can target
        // frozen terminals in one place. We don't define the
        // class itself; consumers / Tailwind config decide.
        const frozenClass = Number(((t.set_recv || {}) as { frozen?: number }).frozen) === 1
          ? ' frozen'
          : '';
        // Same runtime=0 -> "lab/staging" hint as on the Quick view
        // card and the openTerminal modal. The .testing class is
        // styled in admin/index.php with dashed-orange side
        // borders; the only meaningful difference is the
        // border-width on the full All Terminals card vs the
        // dashboard's smaller Quick view card, which the CSS
        // spec leaves to the index.php rule.
        const testingClass = Number(t.runtime) === 0 ? ' testing' : '';
        // Unassigned terminals are operator-less deployments in
        // limbo (mid: known, no operator). We dim them (opacity 0.6)
        // and skip the per-terminal Cash / Games /
        // Today / Avg / Monthly block — the operator group totals on
        // the Groups page (renderGroups) are the place for that
        // accounting data. The unassigned bucket is intentionally
        // reduced to a glance card: name, location, mid, status.
        const isUnassigned = b.key === '__unassigned__';
        const dimClass = isUnassigned ? ' opacity-60' : '';
        const accBlocks = isUnassigned ? '' : `<div class="mt-4 pt-4 border-t border-gray-100 space-y-2">${renderAccBlocks(t, state)}</div>`;
        return `<div onclick="openTerminal(${t.id})" class="${cardBg}${frozenClass}${testingClass}${dimClass} rounded-3xl p-6 card-hover cursor-pointer flex flex-col gap-y-1.5">
          <div class="flex justify-between items-start gap-x-2">
            <div class="flex items-center gap-x-2 min-w-0">
              <span class="text-xl flex-shrink-0">${escapeHtml(icon)}</span>
              <h4 class="font-semibold text-xl truncate" title="${escapeHtml(name)}">${escapeHtml(name)}</h4>
            </div>
            <span class="text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-x-1.5 flex-shrink-0 ${online ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}">
              <span class="inline-block w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}"></span>
              ${escapeHtml(agoLabel)}
            </span>
          </div>
          <div class="flex items-center gap-x-2">
            <span class="text-gray-400 text-sm flex-shrink-0">📍</span>
            <p class="text-gray-500 text-sm truncate">${escapeHtml(t.location || '—')}</p>
          </div>
          <p class="text-gray-400 text-xs font-mono truncate">${escapeHtml(t.mid != null ? String(t.mid) : '—')}</p>
          ${accBlocks}
          <div class="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
            Last seen: ${escapeHtml(formatTs((t as { ts_seen?: unknown }).ts_seen))}
          </div>
          <div class="pt-3 border-t border-gray-100 flex items-center gap-x-2 text-sm">${cardStatusIcons(t)}</div>
        </div>`;
      }).join('');

      // Group header only. Per-group aggregate is intentionally not
      // painted here — the per-terminal cards are the primary
      // signal on this page. The aggregate lives on the Groups page
      // (renderGroups) where the bucket is the focus.
      const heading = `<div class="col-span-full flex items-baseline justify-between mt-2 mb-1">
        <h3 class="text-sm uppercase tracking-widest text-gray-300 font-semibold">${escapeHtml(b.name)}</h3>
        <span class="text-xs text-gray-400">${i18n('dashboard.terminals_suffix', { count: b.terminals.length })}</span>
      </div>`;
      return heading + cards;
    })
    .join('');

  // Render cards into the container first (placeholders need to
  // be in the DOM for the post-paint fetchTodayAccDiff pass
  // below), then prepend the icon legend so the operator can
  // see what the snowflake / no-connection / overlapping coin /
  // gear / wrench icons on each card mean without having to
  // guess. The legend uses the same glyphs as the bottom row of
  // every card (renderAccBlocksLegend) so what the operator
  // sees on the legend maps 1:1 to what they see on the cards.
  container.innerHTML = allCardsHtml;
  container.insertAdjacentHTML('afterbegin', renderAccBlocksLegend());

  // Fire-and-forget: fill in the TODAY cell on every card. The
  // window-level config is read off window.__timestop_config
  // (mirrors the pattern used by renderTabOverview and
  // renderTabLogs for the same fetch). No try/catch — the
  // fetcher swallows its own errors and leaves the placeholder
  // in place on failure.
  const w = window as unknown as { __timestop_config?: ApiFetchConfig };
  const cfg = w.__timestop_config;
  if (cfg) {
    for (const t of renderedTerminals) {
      void fetchTodayAccDiff(t, state, cfg);
    }
  }
}

// =====================================================================
// renderGlobalLogs — Activity Logs page
//
// Logs are grouped by date (newest date first, today default-open,
// older dates collapsed) — same shape as renderTerminalLogs but using
// terminalGlobalLogRow() for the per-row HTML so each row shows
// the terminal's name/location/mid column instead of just the event.
//
// Page layout (rendered by shell.php):
//   <div id="global-logs-body">
//     <div class="date-group" data-date="...">     <-- one per bucket
//       <summary>Today — 2026-06-25   42 entries</summary>
//       <div class="bucket-rows">
//         <div class="...log row...">...</div>     <-- terminalGlobalLogRow
//         ...
//       </div>
//     </div>
//     <div class="date-group" data-date="2026-06-24">...</div>
//     ...
//   </div>
//
// The full-render path wipes and re-emits every bucket. The
// incremental-append path (used by the 30s dashboard ticker) finds
// each new entry's bucket by data-date and inserts the row inside it;
// a new date bucket triggers a full re-render so the header lands in
// the right sort position.
// =====================================================================

export function renderGlobalLogs(state: TimestopState, fullRender: boolean = true): void {
  const container = document.getElementById('global-logs-body');
  if (!container) return;
  // state.globalLogs is optional in the type now (Phase 12.11) — it
  // gets populated by fetchLogs() on navigate(3) and stays empty
  // before then. Default to an empty array so the rest of the
  // function (groupLogsByDate, dedupe sets) can run unchanged.
  const logs = Array.isArray(state.globalLogs) ? state.globalLogs : [];
  if (!state._globalRenderedLogKeys) state._globalRenderedLogKeys = new Set();

  // Row renderer bound to the current state's terminal map. Captured
  // here so renderBucketHtml can call it without seeing state.
  const rowHtml = (l: LogEntry): string => {
    const midStr = l && l.mid != null ? String(l.mid) : '';
    const t = midStr ? state.terminals[midStr] : null;
    return terminalGlobalLogRow(l, t);
  };

  if (fullRender) {
    state._globalRenderedLogKeys.clear();
    if (!logs.length) {
      container.innerHTML = '<div class="px-8 py-6 text-center text-gray-400">No activity logs yet.</div>';
      return;
    }
    const buckets = groupLogsByDate(logs);
    const today = formatLocalDate();
    container.innerHTML = buckets.map(b => renderBucketHtml(b, today, rowHtml)).join('');
    for (const l of logs) state._globalRenderedLogKeys.add(logKey(l));
    return;
  }

  // Incremental append path.
  if (!logs.length) return;
  if (!container.querySelector('.date-group')) {
    renderGlobalLogs(state, true);
    return;
  }

  let appended = 0;
  let newDateAppeared = false;
  const knownDates = new Set<string>();
  container.querySelectorAll('.date-group').forEach(el => {
    const d = el.getAttribute('data-date');
    if (d) knownDates.add(d);
  });
  for (const l of logs) {
    const k = logKey(l);
    if (state._globalRenderedLogKeys.has(k)) continue;
    const date = extractLogDate(l?.ts);
    if (!knownDates.has(date)) {
      newDateAppeared = true;
      break;
    }
    const bucket = container.querySelector(`.date-group[data-date="${CSS.escape(date)}"] .bucket-rows`);
    if (!bucket) {
      newDateAppeared = true;
      break;
    }
    bucket.insertAdjacentHTML('beforeend', rowHtml(l));
    state._globalRenderedLogKeys.add(k);
    appended++;
    const summary = container.querySelector(`.date-group[data-date="${CSS.escape(date)}"] summary`);
    if (summary) {
      const badge = summary.querySelector('span:last-child');
      if (badge) {
        const count = bucket.children.length;
        badge.textContent = `${count} entr${count === 1 ? 'y' : 'ies'}`;
      }
    }
  }

  if (newDateAppeared) {
    renderGlobalLogs(state, true);
    return;
  }
  if (appended === 0 && state._globalRenderedLogKeys.size > logs.length) {
    renderGlobalLogs(state, true);
  }
}

// =====================================================================
// renderTerminalLogs — the per-terminal log list inside the modal
//
// Logs are grouped by date (newest date first, today default-open,
// older dates collapsed). The grouping logic lives in utils/logGrouping.ts;
// this function only handles the DOM side: render the buckets on a
// full re-render, or find the right bucket's container to append new
// rows on an incremental refresh (the 5s log polling).
// =====================================================================

export function renderTerminalLogs(terminal: Terminal | null | undefined, fullRender: boolean): void {
  if (!terminal) return;
  const container = document.getElementById('modal-tab-1');
  if (!container) return;

  // Phase 12.11: per-terminal logs no longer ride on the /api/terminal
  // payload; the modal's Logs tab lazy-loads them via /api/logs?mid=N
  // (see renderTabLogs in terminal-modal.ts). Rows are stashed on the
  // terminal object as `_logs` (underscore prefix to avoid collisions
  // with server-side fields). Until the first fetch lands, we treat
  // the field as an empty array so the "No recent logs available"
  // placeholder renders.
  const logs = Array.isArray((terminal as { _logs?: unknown[] })._logs)
    ? ((terminal as { _logs?: LogEntry[] })._logs as LogEntry[])
    : (Array.isArray(terminal.logs) ? (terminal.logs as LogEntry[]) : []);
  if (!terminal._renderedLogKeys) terminal._renderedLogKeys = new Set();

  if (fullRender) {
    terminal._renderedLogKeys.clear();
    if (!logs.length) {
      container.innerHTML = '<div class="text-gray-400 py-4">No recent logs available</div>';
      return;
    }
    const buckets = groupLogsByDate(logs);
    const today = formatLocalDate();
    container.innerHTML = '<div class="space-y-3">' +
      buckets.map(b => renderBucketHtml(b, today, terminalLogRow)).join('') +
    '</div>';
    for (const l of logs) terminal._renderedLogKeys.add(logKey(l));
    return;
  }

  if (!logs.length) return;
  // No buckets yet — fall back to full render.
  if (!container.querySelector('.date-group')) {
    renderTerminalLogs(terminal, true);
    return;
  }

  // Incremental append. For each new entry, find its date bucket in
  // the DOM and insert the row inside it. If the entry's date has no
  // bucket yet (e.g. midnight rollover while the page is open), do
  // a full re-render so the new bucket appears in the correct sort
  // position.
  let appended = 0;
  let newDateAppeared = false;
  const knownDates = new Set<string>();
  container.querySelectorAll('.date-group').forEach(el => {
    const d = el.getAttribute('data-date');
    if (d) knownDates.add(d);
  });
  for (const l of logs) {
    const k = logKey(l);
    if (terminal._renderedLogKeys.has(k)) continue;
    const date = extractLogDate(l?.ts);
    if (!knownDates.has(date)) {
      newDateAppeared = true;
      break;
    }
    const bucket = container.querySelector(`.date-group[data-date="${CSS.escape(date)}"] .bucket-rows`);
    if (!bucket) {
      newDateAppeared = true;
      break;
    }
    bucket.insertAdjacentHTML('beforeend', terminalLogRow(l));
    terminal._renderedLogKeys.add(k);
    appended++;
    // Update the count badge on the summary.
    const summary = container.querySelector(`.date-group[data-date="${CSS.escape(date)}"] summary`);
    if (summary) {
      const badge = summary.querySelector('span:last-child');
      if (badge) {
        const count = (bucket.children.length);
        badge.textContent = `${count} entr${count === 1 ? 'y' : 'ies'}`;
      }
    }
  }

  if (newDateAppeared) {
    // A new date bucket is needed — re-render to place it in the right
    // sort position with the correct default-open state.
    renderTerminalLogs(terminal, true);
    return;
  }
  if (appended === 0 && terminal._renderedLogKeys.size > logs.length) {
    renderTerminalLogs(terminal, true);
  }
}

// =====================================================================
// renderGroups — page 1 (Groups)
// =====================================================================

export function renderGroups(state: TimestopState): void {
  const container = document.getElementById('all-groups');
  if (!container) return;
  if (!state.groups.length) {
    container.innerHTML = '<div class="col-span-full bg-white rounded-3xl p-8 text-center text-gray-400 border">No groups yet. Click "Add group" to create one.</div>';
    return;
  }

  const avatar = (name: string, bg: string): string => {
    const initial = (name || '?').charAt(0).toUpperCase();
    return `<span class="inline-flex w-5 h-5 flex-shrink-0 rounded-full ${bg} items-center justify-center text-[10px] font-semibold">${escapeHtml(initial)}</span>`;
  };

  container.innerHTML = state.groups.map(g => {
    const assignedTerminals = state.terminals
      ? Object.values(state.terminals).filter(t => String(t.oid) === String(g.id))
      : [];
    const terminalCount = assignedTerminals.length;
    const members = state.users.filter(u => String(u.op_id || u.oid) === String(g.id));

    let membersHtml: string;
    if (!members.length) {
      membersHtml = '<p class="text-gray-400 text-sm italic">No users assigned</p>';
    } else {
      membersHtml = '<ul class="space-y-1.5">' +
        members.map(m => {
          const role = m.role || 'operator';
          const isAdmin = role === 'admin';
          const bg = isAdmin ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
          return `<li class="flex items-center gap-x-2 text-sm text-gray-600 truncate">
            ${avatar(m.username, bg)}
            <span class="truncate">${escapeHtml(m.username)}</span>
            ${isAdmin ? '<span class="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">admin</span>' : ''}
          </li>`;
        }).join('') +
      '</ul>';
    }

    let terminalsHtml: string;
    if (!terminalCount) {
      terminalsHtml = '<p class="text-gray-400 text-sm italic">No terminals assigned</p>';
    } else {
      terminalsHtml = '<ul class="space-y-1.5">' +
        assignedTerminals.map(t => {
          const online = t.status === 'online' || t.status === 'Online';
          // Status dot: green for online, gray for offline. Both
          // states always render the dot (no `hidden` branch) so the
          // row reads as a consistent two-column layout — list first,
          // status indicator on the right — regardless of state.
          const dotBg = online ? 'bg-emerald-500' : 'bg-gray-300';
          const name = displayTerminalName(t);
          const icon = displayTerminalIcon(t);
          return `<li class="flex items-center gap-x-2 text-sm text-gray-600 truncate cursor-pointer hover:text-emerald-600" onclick="openTerminal(${t.id})">
            <span class="inline-flex w-5 h-5 flex-shrink-0 rounded-full bg-gray-100 items-center justify-center text-[10px]">${escapeHtml(icon)}</span>
            <span class="truncate">${escapeHtml(name)}</span>
            <span class="ml-auto w-2 h-2 flex-shrink-0 rounded-full ${dotBg}" title="${online ? i18n('status.online') : i18n('status.offline')}"></span>
           </li>`;
         }).join('') +
      '</ul>';
    }

    // Build a bucket from the assigned terminals so the aggregate
    // block can reuse the same rendering the All Terminals page
    // uses. The bucket key is the group's id so the cell-ids are
    // stable across re-renders (gt-2-today, etc.).
    const bucket: Bucket = { key: String(g.id), name: g.name || `Group ${g.id}`, terminals: assignedTerminals };
    const aggregateHtml = renderGroupAggregateBlock(bucket, state, 'groups');

    // build a stable id for the rename element so the inline
    // onclick handler can find it. The data-group-id carries the
    // id through to editGroupName(this) for the round-trip.
    const nameElId = `group-name-${safeBucketKey(String(g.id))}`;

    return `<div class="bg-white rounded-3xl p-6 card-hover">
      <div class="flex gap-x-4">
        <div class="flex-1 min-w-0">
          <div class="flex justify-between items-start gap-x-2">
            <h4 id="${nameElId}" data-group-id="${escapeHtml(String(g.id))}" class="font-semibold text-xl truncate cursor-pointer hover:text-emerald-600" onclick="editGroupName(this)" title="Click to rename">${escapeHtml(g.name)}</h4>
            <span class="text-xs font-medium px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 flex-shrink-0">#${g.id}</span>
          </div>
          <p class="text-gray-500 text-sm truncate">
            <span class="font-semibold text-gray-700">${terminalCount}</span> terminal${terminalCount === 1 ? '' : 's'}
          </p>
          ${terminalCount > 0 ? `
          <div class="mt-4 pt-4 border-t border-gray-100">
            <p class="text-xs font-medium text-gray-500 mb-2">Group totals</p>
            ${aggregateHtml}
          </div>` : ''}
          <div class="mt-4 pt-4 border-t border-gray-100">
            <p class="text-xs font-medium text-gray-500 mb-2">Assigned users</p>
            ${membersHtml}
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <p class="text-xs font-medium text-gray-500 mb-2">Assigned terminals</p>
            ${terminalsHtml}
          </div>
        </div>
      </div>
    </div>`;
  }).join('');

  // After the DOM is painted, fire the group-level TODAY fetcher
  // for every group that has at least one terminal. The bucket
  // key matches the one used during render so the cell-id
  // resolution inside the fetcher matches (gt-<id>-today).
  const w = window as unknown as { __timestop_config?: ApiFetchConfig };
  const cfg = w.__timestop_config;
  if (cfg) {
    for (const g of state.groups) {
      const ts = state.terminals
        ? Object.values(state.terminals).filter(t => String(t.oid) === String(g.id))
        : [];
      if (!ts.length) continue;
      const bk: Bucket = { key: String(g.id), name: g.name || `Group ${g.id}`, terminals: ts };
      void fetchGroupTodayAccDiff(bk, state, cfg, 'groups');
    }
  }
}

// editGroupName — rename a group in place via prompt() with the
// same orange-flash pattern editTerminalField uses for terminal
// name / location. The element arg is the click target (the <h4>
// in the renderGroups card); its data-group-id carries the group id
// through the round-trip. On success we patch state.groups in
// place and re-render renderGroups + the lists that show the
// bucket label (Quick Terminals / All Terminals) so every view
// stays consistent. Admin-only server-side; the guard lives in
// admin/api/index.php's case 'group_update'.
// =====================================================================

export async function editGroupName(el: HTMLElement): Promise<void> {
  const groupId = el.getAttribute('data-group-id');
  if (!groupId) return;
  const w = window as unknown as {
    timestop?: { state?: TimestopState };
    __timestop_config?: ApiFetchConfig;
    apiFetch?: typeof apiFetch;
  };
  const state = w.timestop?.state;
  const cfg = w.__timestop_config;
  if (!state || !cfg) return;
  const apiFetchFn = w.apiFetch || apiFetch;
  const group = (state.groups || []).find(g => String(g.id) === String(groupId));
  if (!group) return;
  const currentValue = String(group.name || '');
  const newValue = prompt('Rename group:', currentValue);
  if (newValue === null) return;
  if (newValue === currentValue) return;

  const originalClasses = el.className;
  el.classList.add('border-b-2', 'border-orange-500', 'text-orange-600');
  const originalText = el.innerText;
  el.innerText = newValue;

  try {
    const r = await apiFetchFn('group_update', {
      method: 'POST',
      body: { id: Number(groupId), name: newValue },
    }, cfg);
    if (!r.ok) {
      el.innerText = originalText;
      el.className = originalClasses;
      throw new Error((r.data as Record<string, unknown> | null)?.error as string || 'save_failed');
    }
    const g = (state.groups || []).find(x => String(x.id) === String(groupId));
    if (g) g.name = newValue;
    el.classList.remove('border-b-2', 'border-orange-500', 'text-orange-600');
    // Re-render all three views that carry the bucket label.
    const rw = (window as unknown as {
      renderGroups?: (s: TimestopState) => void;
      renderQuickTerminals?: (s: TimestopState) => void;
      renderAllTerminals?: (s: TimestopState) => void;
    });
    if (typeof rw.renderGroups === 'function') rw.renderGroups(state);
    if (typeof rw.renderQuickTerminals === 'function') rw.renderQuickTerminals(state);
    if (typeof rw.renderAllTerminals === 'function') rw.renderAllTerminals(state);
  } catch (e) {
    el.innerText = originalText;
    el.className = originalClasses;
    alert('Rename failed: ' + (e as Error).message);
  }
}

// =====================================================================
// renderUsers — page 5 (Users)
// =====================================================================

const userCard = (u: TimestopState['users'][number], rateCfg: { max_fails?: number; window_sec?: number } = {}): string => {
  const role = u.role || 'operator';
  const isAdmin = role === 'admin';
  const initial = (u.username || '?').charAt(0).toUpperCase();
  const avatarBg = isAdmin ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';

  // Rate-limit badge + fails-in-window counter.
  //   is_rate_limited   comes from the /api/users payload
  //                     (1 if failed_attempts_15m >= RATE_MAX_FAILS)
  //   failed_attempts_15m is the same count Auth::rateLimitOk uses
  //                     to decide whether to short-circuit the next login
  const fails = Number(u.failed_attempts_15m || 0);
  const isLimited = Number(u.is_rate_limited || 0) === 1 || u.is_rate_limited === true;
  const lastFailedLabel = u.last_failed_ts ? formatTs(u.last_failed_ts) : null;
  // Use the same RATE_MAX_FAILS / RATE_WINDOW the backend uses, so the
  // badge text matches the actual threshold even if config.php changes.
  const rateMax = Number(rateCfg.max_fails || 5);
  const rateWinSec = Number(rateCfg.window_sec || 900);
  const rateWinMin = Math.max(1, Math.round(rateWinSec / 60));
  const rateBadge = isLimited
    ? `<div class="mt-3 flex items-center gap-x-2 px-3 py-1.5 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-xs font-medium" title="${rateMax}+ failed logins in the last ${rateWinMin} min — Auth::rateLimitOk is now short-circuiting new attempts for this user.${lastFailedLabel ? ' Last failure: ' + lastFailedLabel : ''}">
         <span>🚫</span>
         <span>Rate-limited (${fails} fails / ${rateWinMin}m)</span>
       </div>`
    : (fails > 0
        ? `<div class="mt-3 flex items-center gap-x-2 px-3 py-1.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium" title="Failed logins in the last ${rateWinMin} min, but below the rate-limit threshold.${lastFailedLabel ? ' Last failure: ' + lastFailedLabel : ''}">
             <span>⚠️</span>
             <span>${fails} fail${fails === 1 ? '' : 's'} / ${rateWinMin}m</span>
           </div>`
        : '');

  return `<div class="bg-white rounded-3xl p-5 card-hover">
    <div class="flex gap-x-3 items-start">
      <div class="w-12 h-12 flex-shrink-0 rounded-2xl ${avatarBg} flex items-center justify-center text-xl font-semibold">${escapeHtml(initial)}</div>
      <div class="flex-1 min-w-0">
        <div class="flex justify-between items-start gap-x-2">
          <h4 class="font-semibold text-lg truncate">${escapeHtml(u.username)}</h4>
        </div>
        <p class="text-gray-500 text-sm truncate">${escapeHtml(u.email || '—')}</p>
      </div>
    </div>
    ${rateBadge}
    <div class="mt-4 flex gap-2">
      <button data-uid="${u.id}" data-action="changepw" class="user-action flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-2xl text-xs font-medium">Change password</button>
      <button data-uid="${u.id}" data-action="delete" data-username="${escapeHtml(u.username)}" class="user-action px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-2xl text-xs font-medium" title="Delete user">🗑</button>
    </div>
  </div>`;
};

const sectionHeader = (title: string, count: number, icon: string): string =>
  `<div class="col-span-full flex items-baseline justify-between mt-2 mb-1">
    <div><span class="text-xl">${icon}</span>
    <span class="text-sm uppercase tracking-widest text-gray-300 font-semibold">${escapeHtml(title)}</span></div>
    <span class="text-xs text-gray-400">${count}</span>
  </div>`;

export function renderUsers(state: TimestopState, onAction: (uid: number | string, action: string) => void): void {
  const container = document.getElementById('all-users');
  if (!container) return;
  if (!state.users.length) {
    container.innerHTML = '<div class="col-span-full bg-white rounded-3xl p-8 text-center text-gray-400 border">No users yet. Click "Add user" to create one.</div>';
    return;
  }

  const admins = state.users.filter(u => (u.role || 'operator') === 'admin');
  const operators = state.users.filter(u => (u.role || 'operator') !== 'admin');

  const byGroup: Record<string, typeof state.users> = {};
  for (const u of operators) {
    const gid = u.op_id || u.oid;
    const gname = gid
      ? ((state.groups.find(g => String(g.id) === String(gid)) || {}).name || `Group #${gid}`)
      : 'Ungrouped';
    if (!byGroup[gname]) byGroup[gname] = [];
    byGroup[gname].push(u);
  }

  let html = '';
  // Pull the rate-limit threshold (RATE_MAX_FAILS / RATE_WINDOW) from
  // the login_attempts payload so the userCard badges stay in sync
  // with whatever the backend is actually enforcing.
  const rateCfg = (state.loginAttempts && state.loginAttempts.rate_limit) || {};
  if (admins.length) {
    html += sectionHeader(i18n('users.section_admins'), admins.length, '👑');
    html += admins.map(u => userCard(u, rateCfg)).join('');
  }
  const groupNames = Object.keys(byGroup).sort();
  for (const gname of groupNames) {
    html += sectionHeader(`Group: ${gname}`, byGroup[gname].length, '👥');
    html += byGroup[gname].map(u => userCard(u, rateCfg)).join('');
  }
  container.innerHTML = html;

  // Wire action buttons via event delegation
  container.querySelectorAll<HTMLButtonElement>('.user-action').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const uid = btn.getAttribute('data-uid');
      const action = btn.getAttribute('data-action');
      if (uid && action) onAction(uid, action);
    });
  });

  // Paint the "Recent login attempts" table under the user grid. This
  // is decoupled so the data can refresh independently of the user
  // list (e.g. on the auto-refresh timer) without a full userCard
  // re-render — which would interrupt any in-progress password prompt.
  renderLoginAttempts(state);
}

// =====================================================================
// renderLoginAttempts — "Recent login attempts" table under the Users
// grid. Reads state.loginAttempts (populated by fetchLoginAttempts).
//
// The table is admin-only. It shows the most recent N attempts across
// ALL usernames — including failed logins for non-existent users, so
// the admin can spot credential-stuffing probes.
//
// "Active rate-limits" mini-section is rendered above the table to
// call out anyone currently locked out — at-a-glance triage.
// =====================================================================

export function renderLoginAttempts(state: TimestopState): void {
  // Self-healing: if the live server's shell.php is older than
  // commit 1f9b372 it won't have the #login-attempts container, so
  // build one on the fly under #all-users. This is a fallback for
  // deploy-mechanics issues (auto-deploy covers admin/api/* but not
  // admin/shell.php on this PC) — when shell.php eventually catches
  // up, the container is already there and we no-op. Idempotent.
  let container = document.getElementById('login-attempts');
  if (!container) {
    const page5 = document.getElementById('page-5');
    const usersGrid = document.getElementById('all-users');
    if (page5 && usersGrid) {
      const wrapper = document.createElement('div');
      wrapper.className = 'mt-10';
      wrapper.innerHTML = `
        <div class="flex items-center gap-x-2 mb-4">
          <span class="text-xl">🔐</span>
          <h3 class="text-lg font-semibold">Recent login attempts</h3>
        </div>
        <div id="login-attempts" class="text-sm text-gray-500">
          <div class="bg-white rounded-3xl p-6 text-center text-gray-400 border">Loading…</div>
        </div>`;
      // Insert right after #all-users so it sits under the user grid
      // in the same place the canonical shell.php markup puts it.
      usersGrid.insertAdjacentElement('afterend', wrapper);
      container = document.getElementById('login-attempts');
    }
  }
  if (!container) return;

  // Phase 12.6: self-fetch if state is empty. VADI's live page
  // was stuck on "No login attempts recorded yet." despite the
  // API responding with data — the post-`fetchUsers` .then()
  // kick in admin/index.php wasn't reliably reaching the
  // post-mutation render call. Belt-and-suspenders: if we get
  // here with empty state, fire our own fetch. The
  // window.__timestop_la_fetching flag prevents the render fn
  // from triggering an infinite loop (a re-render during the
  // fetch's post-mutation call would also have empty state
  // mid-flight, but we skip the self-fetch because the flag
  // is set).
  const attemptsEmpty = !Array.isArray((state.loginAttempts as {attempts?: unknown[]})?.attempts)
    || ((state.loginAttempts as {attempts?: unknown[]}).attempts?.length ?? 0) === 0;
  if (attemptsEmpty && !(window as { __timestop_la_fetching?: boolean }).__timestop_la_fetching) {
    const fn = (window as { fetchLoginAttemptsBundle?: (s: TimestopState, c: { apiBase: string; csrfToken: string | null }) => Promise<void> }).fetchLoginAttemptsBundle;
    if (typeof fn === 'function') {
      // Try the csrfToken from the state param first, then fall
      // back to window.timestop.state. The state param is normally
      // the same object as window.timestop.state, but in some edge
      // cases (auto-refresh timer, the first paint after a
      // navigate, the post-mutation render call) the right token
      // might live in one and not the other.
      const stateCsrf = (state as { csrfToken?: string | null }).csrfToken
        ?? (window as { timestop?: { state?: { csrfToken?: string | null } } }).timestop?.state?.csrfToken
        ?? null;
      if (stateCsrf) {
        (window as { __timestop_la_fetching?: boolean }).__timestop_la_fetching = true;
        // Set a one-shot retry timer before the fetch. The first
        // fetch should normally win; the timer is a safety net
        // for the case where the in-flight Promise never resolves
        // (network stall, 401 swallowed by apiFetch, browser
        // killing the tab during a page lifecycle transition).
        // The flag-clear in .finally is what makes the retry
        // work; if the in-flight fetch DOES complete later, the
        // state mutation will paint the table, and the next
        // render call will see the populated state and skip
        // the retry.
        const stateRef = state;
        const retryKey = '__timestop_la_retry';
        (window as unknown as Record<string, ReturnType<typeof setTimeout> | null>)[retryKey] = setTimeout(() => {
          (window as unknown as Record<string, ReturnType<typeof setTimeout> | null>)[retryKey] = null;
          // Bail if the state is now populated (the in-flight
          // fetch landed between the timer's schedule and its
          // fire).
          const stillEmpty = !Array.isArray((stateRef.loginAttempts as {attempts?: unknown[]})?.attempts)
            || ((stateRef.loginAttempts as {attempts?: unknown[]}).attempts?.length ?? 0) === 0;
          if (!stillEmpty) return;
          const inFlight = (window as { __timestop_la_fetching?: boolean }).__timestop_la_fetching;
          if (inFlight) return; // someone else is fetching, leave them alone
          const freshCsrf = (stateRef as { csrfToken?: string | null }).csrfToken
            ?? (window as { timestop?: { state?: { csrfToken?: string | null } } }).timestop?.state?.csrfToken
            ?? null;
          if (!freshCsrf) return;
          (window as { __timestop_la_fetching?: boolean }).__timestop_la_fetching = true;
          fn(stateRef, { apiBase: '/api/', csrfToken: freshCsrf }).finally(() => {
            (window as { __timestop_la_fetching?: boolean }).__timestop_la_fetching = false;
          });

        }, 5000);
        fn(state, { apiBase: '/api/', csrfToken: stateCsrf }).finally(() => {
          (window as { __timestop_la_fetching?: boolean }).__timestop_la_fetching = false;
          // Cancel the retry if it was scheduled but didn't fire
          // yet (the in-flight fetch succeeded).
          const retry = (window as unknown as Record<string, ReturnType<typeof setTimeout> | null>)[retryKey];
          if (retry) {
            clearTimeout(retry);
            (window as unknown as Record<string, ReturnType<typeof setTimeout> | null>)[retryKey] = null;
          }
        });
      }
    }
  }

  const payload = state.loginAttempts || {};
  const perUser = payload.per_user || {};
  const rateLimit = payload.rate_limit || {};
  const maxFails = Number(rateLimit.max_fails || 5);
  const windowSec = Number(rateLimit.window_sec || 900);

  // Find every user who is currently over the threshold. Match by
  // username (not uid) so failed logins for non-existent users — and
  // therefore absent from state.users — also surface.
  const limitedUsers: Array<{ username: string; fails: number; lastFailedTs: string | null; userRole: string | null; isKnown: boolean }> = [];
  for (const [uname, info] of Object.entries(perUser)) {
    const f = Number(info.failed_attempts_15m || 0);
    if (f >= maxFails) {
      const matched = (state.users || []).find(u => String(u.username) === uname);
      limitedUsers.push({
        username: uname,
        fails: f,
        lastFailedTs: info.last_failed_ts || null,
        userRole: matched ? (matched.role || null) : null,
        isKnown: !!matched,
      });
    }
  }
  // Stable order: most fails first, then most-recent-failure first.
  limitedUsers.sort((a, b) => {
    if (b.fails !== a.fails) return b.fails - a.fails;
    return String(b.lastFailedTs || '').localeCompare(String(a.lastFailedTs || ''));
  });

  // Summary banner.
  const minutes = Math.round(windowSec / 60);
  let summaryHtml = '';
  if (limitedUsers.length) {
    summaryHtml = `<div class="mb-4 flex items-start gap-x-3 px-4 py-3 rounded-2xl bg-red-50 border border-red-200 text-red-800">
      <span class="text-xl leading-none">🚫</span>
      <div class="flex-1 min-w-0">
        <div class="font-semibold text-sm">${limitedUsers.length} username${limitedUsers.length === 1 ? '' : 's'} currently rate-limited</div>
        <div class="text-xs text-red-700 mt-0.5">Threshold: ${maxFails} failed logins in ${minutes} minutes. The next login attempt for these usernames will be rejected without bcrypt work.</div>
      </div>
    </div>`;
  } else {
    summaryHtml = `<div class="mb-4 flex items-start gap-x-3 px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800">
      <span class="text-xl leading-none">✅</span>
      <div class="flex-1 min-w-0">
        <div class="font-semibold text-sm">No usernames currently rate-limited</div>
        <div class="text-xs text-emerald-700 mt-0.5">Threshold: ${maxFails} failed logins in ${minutes} minutes.</div>
      </div>
    </div>`;
  }

  // Per-user rate-limited chip list. Useful when more than one
  // username is locked out — the table below only has the most recent
  // attempts which may not include every offender.
  let chipsHtml = '';
  if (limitedUsers.length) {
    chipsHtml = `<div class="mb-4 flex flex-wrap gap-2">
      ${limitedUsers.map(u => {
        const roleTag = u.userRole
          ? `<span class="ml-1 text-[10px] uppercase tracking-wide opacity-70">${escapeHtml(u.userRole)}</span>`
          : (u.isKnown ? '' : '<span class="ml-1 text-[10px] uppercase tracking-wide opacity-70">unknown user</span>');
        return `<span class="inline-flex items-center gap-x-1 px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">🚫 ${escapeHtml(u.username)}${roleTag} <span class="opacity-60">·</span> ${u.fails} fail${u.fails === 1 ? '' : 's'}</span>`;
      }).join('')}
    </div>`;
  }

  // Recent attempts table.
  const attempts: LoginAttempt[] = Array.isArray(payload.attempts) ? payload.attempts : [];
  let tableHtml: string;
  if (!attempts.length) {
    tableHtml = '<div class="px-4 py-6 text-center text-sm text-gray-400">No login attempts recorded yet.</div>';
  } else {
    const rows = attempts.map(a => {
      const success = Number(a.success || 0) === 1 || a.success === true;
      const badge = success
        ? '<span class="inline-flex items-center gap-x-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">✓ ok</span>'
        : '<span class="inline-flex items-center gap-x-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[11px] font-medium">✗ fail</span>';
      const ts = a.ts ? formatTs(a.ts) : '—';
      const ip = a.ip ? escapeHtml(String(a.ip)) : '—';
      const ua = a.ua ? escapeHtml(String(a.ua).slice(0, 60)) : '—';
      // Surface "this attempt is one of the rate-limited offenders" by
      // colour-striping the username cell.
      const isOffender = !success && limitedUsers.some(u => u.username === a.username);
      const userCellCls = isOffender
        ? 'px-4 py-2 text-sm font-medium text-red-700'
        : 'px-4 py-2 text-sm text-gray-800';
      return `<tr class="border-t border-gray-100">
        <td class="${userCellCls}">${escapeHtml(String(a.username || '—'))}</td>
        <td class="px-4 py-2">${badge}</td>
        <td class="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">${escapeHtml(ts)}</td>
        <td class="px-4 py-2 text-xs font-mono text-gray-600">${ip}</td>
        <td class="px-4 py-2 text-xs text-gray-500 truncate max-w-xs" title="${ua}">${ua}</td>
      </tr>`;
    }).join('');
    tableHtml = `<div class="overflow-x-auto"><table class="w-full text-left">
      <thead>
        <tr class="text-[11px] uppercase tracking-wide text-gray-400">
          <th class="px-4 py-2 font-medium">Username</th>
          <th class="px-4 py-2 font-medium">Result</th>
          <th class="px-4 py-2 font-medium">When</th>
          <th class="px-4 py-2 font-medium">IP</th>
          <th class="px-4 py-2 font-medium">User agent</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table></div>`;
  }

  container.innerHTML = `${summaryHtml}${chipsHtml}<div class="bg-white rounded-3xl p-4">${tableHtml}</div>`;
}

// =====================================================================
// renderNotify — dashboard notify panel (Phase 12.17)
//
// Reads state.notify (the per-user pending feed) and paints it into
// the #dashboard-notify / #dashboard-notify-body containers declared
// in admin/shell.php.
//
// Shape contract: the rows are already in LogEntry shape
// ({ts, mid, event, logval, type, id, _mach_name, _mach_loc}) by
// the time they reach this function — /api/notify does the
// projection server-side (see admin/api/index.php). That lets us
// reuse the same row helper the Activity Logs page uses
// (terminalNotificationLogRow, which is a near-twin of
// terminalGlobalLogRow with the click-to-dismiss affordance
// baked in), so the two views are visually identical
// (TIME / TERMINAL / EVENT / DETAIL columns) and the click
// contract for the notify panel can't silently desync from the
// row shape.
//
// Date grouping: rows are bucketed by the date portion of `ts` via
// groupLogsByDate(), then each bucket is rendered as a collapsible
// <details> via renderNotifyBucket() (the notify-specific twin of
// renderBucketHtml() in admin/src/utils/logGrouping.ts) with the
// rowHtml closure above. All buckets default to open so the small
// ~50-row notify feed is fully visible at a glance — users can
// still collapse any bucket manually. Removing a row via
// markNotifyRead() uses the incremental path in removeNotifyRow()
// so the other buckets' open/closed state survives.
//
// Click-to-mark-read is wired via a single delegated `click`
// listener on #dashboard-notify-body (installed once in
// installNotifyClickHandler). The listener reads
// data-notify-id off the clicked <div> and calls
// window.markNotifyRead.
//
// Hidden when the feed is empty — there's no value in showing an
// empty card.
// =====================================================================

// Build a Terminal-shaped stub from the row's server-joined
// _mach_name / _mach_loc. terminalGlobalLogRow() only reads
// `t.name` and `t.location`, so a two-field stub is enough to keep
// the row legible when the SPA hasn't loaded the full Terminal
// object into state.terminals yet (e.g. terminal moved out of
// group, or the dashboard ticker fired before fetchData landed).
function notifyRowTerminal(row: { mid?: unknown; _mach_name?: unknown; _mach_loc?: unknown }): Terminal | null {
  const mid = Number(row.mid ?? 0);
  if (!mid) return null;
  const name = typeof row._mach_name === 'string' && row._mach_name ? row._mach_name : '';
  const location = typeof row._mach_loc === 'string' ? row._mach_loc : '';
  return { id: mid, name, location } as Terminal;
}

export function renderNotify(state: TimestopState): void {
  const panel  = document.getElementById('dashboard-notify');
  const body   = document.getElementById('dashboard-notify-body');
  const countEl = document.getElementById('dashboard-notify-count');
  if (!panel || !body) return;

  const rows = Array.isArray(state.notify) ? state.notify : [];

  if (rows.length === 0) {
    panel.classList.add('hidden');
    body.innerHTML = '';
    if (countEl) countEl.textContent = '';
    return;
  }
  panel.classList.remove('hidden');

  if (countEl) {
    countEl.textContent = i18n('dashboard.pending_count', { count: rows.length });
  }

  // The rows are already LogEntry-shaped server-side, so the same
  // terminalGlobalLogRow() the Activity Logs page uses produces
  // identical HTML here. The mid lookup prefers state.terminals
  // (full Terminal object) and falls back to a stub built from the
  // row's _mach_name / _mach_loc so the column is never blank.
  //
  // We use terminalNotificationLogRow() (a dedicated variant) rather
  // than calling terminalGlobalLogRow() + post-injecting
  // data-notify-id with .replace(), because the post-injection is
  // fragile: if the row's outer class string ever drifts (e.g. the
  // flex layout switched to a grid layout), the .replace() silently
  // no-ops and every notify row loses its click-to-dismiss
  // affordance with no visible error. Baking the click target into
  // the row renderer means the row shape and the click contract
  // live in the same file, so they cannot desync.
  const rowHtml = (entry: LogEntry): string => {
    const midStr = entry && entry.mid != null ? String(entry.mid) : '';
    const tFromState = midStr ? state.terminals[midStr] : null;
    const t = tFromState || notifyRowTerminal(entry as { mid?: unknown; _mach_name?: unknown; _mach_loc?: unknown });
    return terminalNotificationLogRow(entry, t);
  };

  // Cast rows to LogEntry[] for the existing grouping helper —
  // /api/notify already projects to that shape. We do this here
  // rather than at fetch time so any non-server producer (e.g. a
  // future WebSocket push) can drop rows into state.notify in any
  // shape and the renderer will still bucket them by ts.
  //
  // The notify panel is the user's primary action surface for a
  // small (~50 rows) feed, so every date bucket is rendered open.
  // renderNotifyBucket() (admin/src/utils/logGrouping.ts) bakes
  // that open-by-default behaviour in and drops the `today` label
  // distinction — both audit views (Activity Logs, per-terminal
  // logs) keep the legacy "today open, others collapsed" default
  // via renderBucketHtml(), so a notify-specific helper is the
  // cleanest split. Removing a row via markNotifyRead() uses the
  // incremental path in removeNotifyRow() so the other buckets'
  // open/closed state survives.
  const asEntries = rows as LogEntry[];
  const buckets = groupLogsByDate(asEntries);
  body.innerHTML = buckets.map(b => renderNotifyBucket(b, rowHtml)).join('');
}

// removeNotifyRow — incremental DOM update for the dashboard
// notify panel when a single row is marked as read.
//
// renderNotify() does a full innerHTML rebuild, which destroys
// every <details> open/closed state in the panel. That made
// clicking a row to mark it as read collapse all the other date
// buckets the user had expanded. This function does the minimum
// invasive update — find the row by data-notify-id, remove it,
// and if its parent bucket is now empty, remove the bucket and
// refresh the count header. Other buckets are untouched, so any
// <details> the user expanded stays expanded and the scroll
// position is preserved.
//
// The state.notify array is mutated by the caller (see
// data.ts::markNotifyRead). This function only touches the DOM.
export function removeNotifyRow(state: TimestopState, id: number): void {
  const panel   = document.getElementById('dashboard-notify');
  const body    = document.getElementById('dashboard-notify-body');
  const countEl = document.getElementById('dashboard-notify-count');
  if (!panel || !body) return;

  const row = body.querySelector<HTMLElement>(`[data-notify-id="${id}"]`);
  if (!row) return;
  // The row lives inside <div class="bucket-rows">. The bucket
  // is the parent <details>. Remove the row first; if the bucket
  // has no rows left, drop the whole <details> too.
  const bucket = row.closest<HTMLElement>('details.date-group');
  row.remove();

  if (bucket) {
    const remaining = bucket.querySelectorAll('[data-notify-id]').length;
    if (remaining === 0) {
      bucket.remove();
    } else {
      // Refresh the bucket's count summary so the "N entries"
      // header matches the new row count. renderBucketHtml uses
      // `entr` / `entries` pluralisation; mirror that.
      const summary = bucket.querySelector('summary');
      const counter = summary ? summary.querySelector('span:last-child') : null;
      if (counter) {
        counter.textContent = `${remaining} entr${remaining === 1 ? 'y' : 'ies'}`;
      }
    }
  }

  // Update the panel-level "N pending" counter and hide the
  // panel entirely when the last row is gone.
  const remaining = body.querySelectorAll('[data-notify-id]').length;
  if (remaining === 0) {
    panel.classList.add('hidden');
    body.innerHTML = '';
    if (countEl) countEl.textContent = '';
    // state.notify should also be empty at this point — the
    // caller already spliced. We don't reassign here because
    // other code may have a reference to the same array.
  } else if (countEl) {
    countEl.textContent = `${remaining} pending`;
  }
}

// installNotifyClickHandler — single delegated click listener on
// #dashboard-notify-body that routes row clicks to
// window.markNotifyRead. Installed once at module init time (see
// main.ts). The listener reads data-notify-id off the closest
// row-matching <div> in the click target's ancestry. We can't use
// the existing notifyReadClick(N) onclick pattern from the prior
// phase because the rows are now produced by
// terminalGlobalLogRow(), a shared helper that has no knowledge of
// the notify panel's click contract — injection is the cleanest
// way to keep the row renderer free of notify-specific concerns.
let _notifyClickInstalled = false;
export function installNotifyClickHandler(): void {
  if (_notifyClickInstalled) return;
  _notifyClickInstalled = true;
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    // Find the nearest row emitted by terminalNotificationLogRow. We
    // look for any [data-notify-id] ancestor — the row itself
    // sets the attribute, and the click is normally on a child
    // <span>.
    const row = target.closest<HTMLElement>('[data-notify-id]');
    if (!row) return;
    // Archived rows (already read; /api/notify populates them from
    // notify_arch with a readon field) carry the `archived` class
    // for styling — the same class also signals "no action
    // available" to this handler. A click on a stale row is
    // silently swallowed: the server would 404 anyway (the row no
    // longer exists in `notify`), but skipping the network round
    // trip is cheaper and the user gets no feedback flash that
    // would suggest the click did something.
    if (row.classList.contains('archived')) return;
    const id = Number(row.getAttribute('data-notify-id') || '0');
    if (!Number.isFinite(id) || id <= 0) return;
    const fn = (window as unknown as { markNotifyRead?: (s: TimestopState, c: { apiBase: string; csrfToken: string | null }, id: number) => unknown }).markNotifyRead;
    if (!fn) return;
    const st = (window as unknown as { timestop: { state: TimestopState } }).timestop.state;
    const csrf = st.csrfToken;
    void fn(st, { apiBase: '/api/', csrfToken: csrf }, id);
  });
}




// =====================================================================
// renderAll — top-level "redraw everything" called by fetchData()
// =====================================================================

export function renderAll(state: TimestopState): void {
  renderKPIs(state);
  renderQuickTerminals(state);
  renderAllTerminals(state);
  // renderGlobalLogs uses an incremental path because fetchData() already
  // merged the new array entries in place via replaceArrayInPlace() —
  // wiping the DOM here would also reset the user's collapsed/expanded
  // state on every date-group <details>. Only an empty logs list or a
  // new-date arrival triggers a full re-render inside renderGlobalLogs.
  renderGlobalLogs(state, false);

  // If the terminal detail modal is open, push any freshly-fetched log
  // entries into the live log list without re-rendering the whole pane.
  if (state.currentTerminal) {
    const backdrop = document.getElementById('modal-backdrop');
    if (backdrop && !backdrop.classList.contains('hidden')) {
      renderTerminalLogs(state.currentTerminal, false);
    }
  }
}

// Re-export some things from types for consumers
export type { TimestopState } from './state';
export type { Terminal, TerminalsById, Group };